import type { ReactElement, PropsWithChildren } from 'react'
import { useEffect, useId, useState } from 'react'
import { Collapse } from '@mui/material'
import { type SystemCssProperties } from '@dhl-ui/theme'
import { useSpacingToken } from '../../theme/hooks/use-spacing-token'
import type { DataTestIdAttribute, BaseProps } from '../../types'
import { Box } from '../../primitives/Box'
import { BareButton } from '../../primitives/Button'
import { Divider } from '../../primitives/Divider'
import { ExpandMore } from '../../primitives/ExpandMore'
import { StackRow } from '../../primitives/Stack'
import { Typography } from '../../primitives/Typography'
import { AccordionContent, type AccordionContentProps } from './AccordionContent'

export type AccordionProps = PropsWithChildren<
  {
    title: string

    /**
     * Start open or control it from the outside
     */
    isOpen?: boolean

    /**
     * The Accordion has by default a divider
     * @default true
     */
    hasDivider?: boolean

    /**
     * The title can be bold
     * @default true
     */
    emphasis?: boolean

    /**
     * Option to get a 'dark' background instead of its default 'light'
     * @default 'light
     */
    background?: 'light' | 'dark'

    onOpened?: () => void
    onClosed?: () => void
    onClick?: () => void

    slotProps?: {
      /**
       * Technical options to mount the collapsable its children or not
       * they will cleaned up on exit anyway
       */
      collapse?: {
        mountOnEnter?: boolean
      }

      /**
       * Influence on the paddings being used in the content section (your children) of the accordion
       */
      content?: Omit<AccordionContentProps, 'children'>

      /**
       * You can place an `IconSeverity` to the Left, please follow the UX designs :)
       */
      icon?: ReactElement
    }
  } & Omit<BaseProps<HTMLDivElement>, 'sx'> & { sx?: Pick<SystemCssProperties, 'flex'> } & DataTestIdAttribute
>

/**
 * Accordion
 *
 * Demos:
 * - [Accordion](https://design-system.accept.cl1.intra.dhlparcel.io/?path=/docs/constructs-accordion--docs)
 *
 */
export const Accordion = ({
  children,
  title,
  hasDivider,
  isOpen,
  background = 'light',
  slotProps,
  emphasis = true,
  onClick,
  onOpened,
  onClosed,
  ...rest
}: AccordionProps) => {
  const [open, setIsOpen] = useState(isOpen ?? false)
  const spacingToken = useSpacingToken()
  const id = useId()
  const labelControl = `${id}-content`
  const labelHeader = `${id}-header`

  const handleToggle = () => {
    const state = !open

    if (state) {
      onOpened?.()
    } else {
      onClosed?.()
    }

    onClick?.()

    setIsOpen((prevState) => !prevState)
  }

  useEffect(() => {
    if (isOpen !== undefined) {
      setIsOpen(isOpen)
    }
  }, [isOpen])

  return (
    <Box
      {...rest}
      sx={{
        width: '100%',
        backgroundColor: background === 'light' ? 'background.paper' : 'background.default',
      }}
    >
      <BareButton
        onClick={handleToggle}
        aria-expanded={open}
        aria-controls={labelControl}
        id={labelHeader}
        sx={({ misc }) => ({
          width: '100%',
          alignItems: 'flex-start',
          justifyContent: 'start',
          py: 2,
          px: spacingToken,
          ...misc.createHoverOrActiveState({
            backgroundColor: 'action.hover',
          }),

          ...misc.createFocusVisibleState({
            backgroundColor: 'action.focus',
            outline: misc.borderStyle('info.main', 3),
            outlineOffset: misc.borderSize(-3),
            borderRadius: misc.borderRadius(2),
          }),
        })}
      >
        <StackRow gap={2} sx={{ width: '100%', textAlign: 'left' }}>
          {slotProps?.icon ? slotProps.icon : null}

          <Typography variant={emphasis ? 'h6' : 'body1'} sx={{ flex: 1 }}>
            {title}
          </Typography>

          <ExpandMore isOpen={open} />
        </StackRow>
      </BareButton>

      <Collapse in={open} {...slotProps?.collapse} unmountOnExit>
        <AccordionContent {...slotProps?.content}>{children}</AccordionContent>
      </Collapse>
      {hasDivider && <Divider />}
    </Box>
  )
}
